import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  useTheme,
  keyframes,
  styled,
  Chip
} from '@mui/material';
import {
  Speed,
  TrendingUp,
  Assessment,
  EnergySavingsLeafRounded,
  Battery90,
  FlashOnRounded,
  EnergySavingsLeaf,
  SecurityRounded
} from '@mui/icons-material';

// Keyframe animations
const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled components
const ShowcaseCard = styled(Card)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '24px',
  background: `linear-gradient(135deg, ${theme.palette.background.paper}90 0%, ${theme.palette.background.paper}75 40%, ${theme.palette.primary.main}10 75%, ${theme.palette.primary.main}08 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${theme.palette.primary.main}20`,
  boxShadow: `0 8px 32px ${theme.palette.primary.main}15`,
  transition: 'all 0.4s ease',
  position: 'relative',
  overflow: 'hidden',

  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 60px ${theme.palette.primary.main}25`,
    border: `1px solid ${theme.palette.primary.main}40`,
  },

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: '24px 24px 0 0',
  }
}));



const DataVisualization = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '280px', // Größere Höhe für vertikales Layout (3 Reihen)
  borderRadius: '16px',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}06 30%, ${theme.palette.secondary.main}06 70%, ${theme.palette.secondary.main}05 100%)`,
  border: `1px solid ${theme.palette.primary.main}20`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 70%, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 30%, transparent 50%)`,
    animation: `${pulse} 4s ease-in-out infinite`,
  }
}));

interface MetricData {
  label: string;
  value: number;
  maxValue: number;
  suffix: string;
  color: 'primary' | 'secondary' | 'success' | 'info' | 'warning';
  icon: React.ReactElement;
  description: string;
}

interface TechFeature {
  title: string;
  description: string;
  metrics: MetricData[];
  highlight: string;
}

const TechnologyShowcase: React.FC = () => {
  const theme = useTheme();
  const [animatedValues, setAnimatedValues] = useState<{ [key: string]: number }>({});

  const techFeatures: TechFeature[] = [
    {
      title: 'Hybrid-Speichersystem Performance',
      description: 'Technologie-Empfehlungen basierend auf Ihrem Missionsprofil. Revolutionäre Hybrid-Speichertechnologie für maximale Effizienz',
      highlight: 'Bis zu 40% Effizienzsteigerung',
      metrics: [
        {
          label: 'Effizienzsteigerung',
          value: 40,
          maxValue: 50,
          suffix: '%',
          color: 'success',
          icon: <TrendingUp />,
          description: 'Verbesserung gegenüber herkömmlichen Systemen'
        },
        {
          label: 'Kosteneinsparung',
          value: 35,
          maxValue: 50,
          suffix: '%',
          color: 'primary',
          icon: <Assessment />,
          description: 'Reduzierung der Betriebskosten'
        },
        {
          label: 'Lebensdauer',
          value: 40,
          maxValue: 100,
          suffix: '%',
          color: 'info',
          icon: <Battery90 />,
          description: 'Bis zu 40% längere Lebensdauer* (*abhängig vom Mission Profile)'
        }
      ]
    },
    {
      title: 'Systematische Analyseleistung',
      description: 'Bewährte Algorithmen und Berechnungsmodelle für zuverlässige Bewertungen und Empfehlungen.',
      highlight: 'Beta-Version verfügbar',
      metrics: [
        {
          label: 'Analyse-Geschwindigkeit',
          value: 85,
          maxValue: 100,
          suffix: '%',
          color: 'primary',
          icon: <Speed />,
          description: 'Geschätzte Analysezeit 1 - 2 Minuten'
        },
        {
          label: 'Algorithmus-Basis',
          value: 100,
          maxValue: 100,
          suffix: '%',
          color: 'success',
          icon: <FlashOnRounded />,
          description: 'Bewährte Berechnungsmodelle'
        },
        {
          label: 'Potential-Bewertung',
          value: 80,
          maxValue: 100,
          suffix: '%',
          color: 'success',
          icon: <EnergySavingsLeaf />,
          description: 'Identifikation von Einsparpotenzialen'
        }
      ]
    },
    {
      title: 'Sicherheit & Compliance',
      description: 'Höchste Sicherheitsstandards und vollständige DSGVO-Konformität für Ihre Daten',
      highlight: 'ISO 27001 Zertifiziert',
      metrics: [
        {
          label: 'Datenschutz',
          value: 100,
          maxValue: 100,
          suffix: '%',
          color: 'info',
          icon: <SecurityRounded />,
          description: 'DSGVO-konforme Datenverarbeitung'
        },
        {
          label: 'Verschlüsselung',
          value: 256,
          maxValue: 256,
          suffix: 'bit',
          color: 'warning',
          icon: <SecurityRounded />,
          description: 'Ende-zu-Ende Verschlüsselung'
        },
        {
          label: 'Verfügbarkeit',
          value: 99.9,
          maxValue: 100,
          suffix: '%',
          color: 'success',
          icon: <TrendingUp />,
          description: 'Systemverfügbarkeit SLA'
        }
      ]
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      const newValues: { [key: string]: number } = {};
      techFeatures.forEach((feature, featureIndex) => {
        feature.metrics.forEach((metric, metricIndex) => {
          const key = `${featureIndex}-${metricIndex}`;
          newValues[key] = metric.value;
        });
      });
      setAnimatedValues(newValues);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const getAnimatedValue = (featureIndex: number, metricIndex: number) => {
    const key = `${featureIndex}-${metricIndex}`;
    return animatedValues[key] || 0;
  };

  return (
    <Box
      sx={{
        py: 10,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Animation */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 80%, ${theme.palette.primary.main}08 0%, transparent 50%),
                       radial-gradient(circle at 80% 20%, ${theme.palette.secondary.main}06 0%, transparent 50%)`,
          animation: `${pulse} 8s ease-in-out infinite`,
        }}
      />

      <Container maxWidth={false} sx={{
        position: 'relative',
        zIndex: 2,
        px: { xs: 2, sm: 4, md: 6 },
        maxWidth: '1400px',
        mx: 'auto',
        py: 8
      }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="🚀 Technologie der Zukunft"
            color="primary"
            variant="outlined"
            sx={{
              mb: 3,
              fontSize: '1rem',
              py: 1,
              px: 3,
              animation: `${float} 3s ease-in-out infinite`,
              background: `${theme.palette.primary.main}10`,
              border: `2px solid ${theme.palette.primary.main}30`,
            }}
          />

          <Typography
            variant="h2"
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
              mb: 3,
              animation: `${slideInUp} 1s ease-out`,
            }}
          >
            Hybrid-Speichersysteme der nächsten Generation
          </Typography>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.7,
              animation: `${slideInUp} 1s ease-out 0.2s both`,
            }}
          >
            Erleben Sie die revolutionäre Kombination aus fortschrittlicher KI-Analyse und
            innovativer Hybrid-Speichertechnologie für optimale Energielösungen
          </Typography>
        </Box>

        {/* Technology Features Grid */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ alignItems: 'stretch' }}>
          {techFeatures.map((feature, featureIndex) => (
            <Grid item xs={12} lg={4} key={featureIndex} sx={{ display: 'flex' }}>
              <Box
                sx={{
                  animation: `${slideInUp} 1s ease-out ${0.3 + featureIndex * 0.2}s both`,
                  width: '100%',
                  display: 'flex',
                }}
              >
                <ShowcaseCard>
                  <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Feature Header - Kompletter Header-Bereich */}
                    <Box sx={{
                      mb: 0.5,
                      height: { xs: '380px', sm: '350px', md: '320px' },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      {/* Titel-Bereich */}
                      <Box sx={{ flex: '0 0 auto' }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 2,
                            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
                            minHeight: { xs: '60px', sm: '55px', md: '50px' },
                            display: 'flex',
                            alignItems: 'flex-start',
                          }}
                        >
                          {feature.title}
                        </Typography>

                        <Chip
                          label={feature.highlight}
                          size="small"
                          color="primary"
                          sx={{
                            alignSelf: 'flex-start',
                            mb: 3,
                            background: `${theme.palette.primary.main}20`,
                            color: 'primary.main',
                            fontWeight: 600,
                          }}
                        />
                      </Box>

                      {/* Beschreibungs-Bereich */}
                      <Box sx={{ flex: '1 1 auto', display: 'flex', alignItems: 'flex-start' }}>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            fontSize: '18px',
                            lineHeight: 1.6,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Data Visualization */}
                    <DataVisualization sx={{ mb: 3, flex: '0 0 auto' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                          width: '100%',
                          p: { xs: 2, sm: 2.5, md: 3 },
                          alignItems: 'stretch',
                        }}
                      >
                        {feature.metrics.map((metric, metricIndex) => (
                          <Box
                            key={metricIndex}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: { xs: 1, sm: 1.5, md: 2 },
                              borderRadius: 3,
                              background: `${theme.palette[metric.color].main}15`,
                              border: `1px solid ${theme.palette[metric.color].main}30`,
                              animation: `${float} 4s ease-in-out infinite`,
                              animationDelay: `${metricIndex * 0.5}s`,
                              height: { xs: '70px', sm: '75px', md: '80px' },
                              width: '100%',
                              gap: 1,
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                              '&:hover': {
                                transform: 'translateX(5px)',
                                background: `${theme.palette[metric.color].main}20`,
                                transition: 'all 0.3s ease',
                                '& .icon-container': {
                                  animation: `${rotate} 4s linear infinite`,
                                }
                              },
                            }}
                          >
                            <Box
                              className="icon-container"
                              sx={{
                                color: `${metric.color}.main`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                background: `${theme.palette[metric.color].main}20`,
                                transition: 'transform 0.3s ease',
                              }}
                            >
                              {metric.icon}
                            </Box>
                            <Box sx={{
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                              pt: 1,
                              minWidth: 0
                            }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: `${metric.color}.main`,
                                  fontWeight: 700,
                                  fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1,
                                  mb: 0.5,
                                }}
                              >
                                {getAnimatedValue(featureIndex, metricIndex).toFixed(metric.suffix === '%' ? 1 : 0)}
                                {metric.suffix}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' },
                                  lineHeight: 1.3,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {metric.label}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </DataVisualization>


                  </CardContent>
                </ShowcaseCard>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TechnologyShowcase;